import { CSSObject } from '@mui/material';

import type { Theme } from '../../themes/themes';

export const createTextFieldTheme = (theme: Theme): Partial<Theme> => ({
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-positionEnd + .MuiInputBase-input': {
            paddingLeft: theme.spacing(2),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: {
                color: 'primary',
              },
              style: {
                ...theme.typography.button,
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.border.main,
                },
                '&:not(.Mui-disabled):hover': {
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.border.hover,
                    borderWidth: '1px',
                    color: '#000',
                  },
                  label: {
                    color: '#000',
                  },
                },
                '&.Mui-error': {
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.error['main'],
                    borderWidth: '1px',
                  },
                },
                '&.Mui-focused': {
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.action.focus,
                    borderWidth: '1px',
                  },
                  '&:not(.Mui-disabled):hover': {
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.action.focus,
                      borderWidth: '1px',
                      color: '#000',
                    },
                  },
                },
                input: {
                  '&[type="date"]': {
                    minHeight: theme.spacing(5), // iphone quick-fix (iphone does not have placeholder in date field)
                    '&::-webkit-date-and-time-value': {
                      textAlign: 'left',
                    },
                  },
                  '&::placeholder': {
                    opacity: 1,
                    color: theme.palette.action.hint,
                  },
                  '&::-webkit-calendar-picker-indicator': {
                    opacity: 0,
                  },
                  '&::-webkit-datetime-edit-fields-wrapper': {
                    padding: 0,
                  },
                },
              },
            },
          ],
        },
        adornedEnd: {
          paddingRight: 0,
        },
        multiline: {
          padding: 0,
        },
        inputMultiline: {
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
          paddingLeft: theme.spacing(5),
          paddingRight: theme.spacing(5),
        },
        input: {
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
          paddingLeft: theme.spacing(5),
          paddingRight: theme.spacing(5),
          [theme.breakpoints.up('lg')]: {
            paddingTop: theme.spacing(5),
            paddingBottom: theme.spacing(5),
          },
          '&, .if6 &': {
            ...theme.typography.button,
            height: 'auto',
            boxSizing: 'content-box',
            borderWidth: 0,
            borderColor: 'transparent',
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
            backgroundColor: 'unset',
            [theme.breakpoints.up('lg')]: {
              ...(theme.typography.button[theme.breakpoints.up('lg')] as CSSObject),
              paddingTop: theme.spacing(5),
              paddingBottom: theme.spacing(5),
            },
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: {
                color: 'primary',
              },
              style: {
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.search.input['backgroundDefault'],
                padding: theme.spacing(5),
                color: theme.palette.action.hint,

                '&.Mui-focused': {
                  backgroundColor: theme.palette.search.input['backgroundDefault'],
                  [theme.breakpoints.down('sm')]: {
                    paddingBlock: '16px', // Fix: IOS not ZOOM
                  },
                },

                '& input.MuiFilledInput-input:focus': {
                  [theme.breakpoints.down('sm')]: {
                    // fontSize: 'inherit', // Fix: IOS not ZOOM
                  },
                },

                '&:not(.Mui-disabled):hover, &:not(.Mui-disabled):hover .MuiFilledInput-input': {
                  backgroundColor: theme.palette.search.input['backgroundActive'],
                },

                input: {
                  padding: 0,
                  color: theme.palette.text.primary,
                  '&::placeholder': {
                    opacity: 1,
                    textAlign: 'center',
                  },
                },

                [theme.breakpoints.up('lg')]: {
                  borderRadius: theme.spacing(4),
                  padding: theme.spacing(6),
                  paddingTop: theme.spacing(7),
                  paddingBottom: theme.spacing(7),
                },

                ['.MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)']:
                  {
                    marginTop: 0,
                  },
              },
            },
          ],
        },
        input: {
          '&, .if6 &': {
            ...theme.typography.body2,
            backgroundColor: theme.palette.search.input['backgroundDefault'],
            boxSizing: 'border-box',
            borderWidth: 0,
            borderColor: 'transparent',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-input.MuiFilledInput-input': {
            padding: 0,
          },
          '& .MuiAutocomplete-input.MuiOutlinedInput-input': {
            padding: 0,
          },
        },
        paper: {
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme.palette.search.list['backgroundHover'],
          },
          '& .MuiAutocomplete-option': {
            backgroundColor: theme.palette.background.default,
          },
          '& .MuiAutocomplete-option:hover': {
            backgroundColor: theme.palette.search.list['backgroundHover'],
          },
        },
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
            [theme.breakpoints.up('lg')]: {
              paddingTop: theme.spacing(5),
              paddingBottom: theme.spacing(5),
            },
          },
          '&.MuiFilledInput-root': {
            padding: theme.spacing(5),

            [theme.breakpoints.up('lg')]: {
              borderRadius: theme.spacing(4),

              paddingLeft: theme.spacing(6),
              paddingRight: theme.spacing(6),
              paddingTop: theme.spacing(7),
              paddingBottom: theme.spacing(7),
            },
          },
        },
        input: {
          padding: 0,
          transitionProperty: 'background-color',
          transitionDuration: '200ms',
          transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
          transitionDelay: '0ms',
        },
        endAdornment: {
          '.MuiOutlinedInput-root.MuiInputBase-adornedEnd &': {
            top: 'calc(50% - 13.285px)',
            [theme.breakpoints.up('lg')]: {
              top: 'calc(50% - 29.285px)',
            },
          },
          '.MuiFilledInput-root.MuiInputBase-adornedEnd &': {
            right: theme.spacing(4),
            [theme.breakpoints.up('lg')]: {
              right: theme.spacing(6),
            },
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: {
          marginRight: theme.spacing(4),
        },
        root: {
          p: {
            color: '#000',
            fontSize: 16,
          },
          '& svg': {
            fontSize: 20,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.search.dropdown['label'],

          '&:not(.Mui-disabled):hover': {
            color: '#000',
          },
          '&.Mui-error': {
            color: theme.palette.text.primary,
          },
          '&.Mui-focused': {
            '.MuiFormLabel-asterisk': {
              color: theme.palette.action.focus,
            },
            '&:hover': {
              color: theme.palette.action.focus,
            },
          },
          '&, .if6 &': {
            ...theme.typography.button,
          },
        },
        outlined: {
          transform: 'translate(14px, 12px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },

          [theme.breakpoints.up('lg')]: {
            transform: 'translate(14px, 18px) scale(1)',
          },
        },
        filled: {
          top: theme.spacing(5),
          transform: 'translate(18px, 0%) scale(1)',
          transitionProperty: 'top, color, transform, max-width',
          '&.MuiInputLabel-shrink': {
            top: 0,
            transform: 'translate(12px, 7px) scale(0.75)',
          },

          [theme.breakpoints.up('lg')]: {
            top: theme.spacing(7),
            transform: 'translate(24px, 0%) scale(1)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...(theme.typography.h6 as CSSObject),

          color: theme.palette.grey[800],

          '&.Mui-error': {
            color: theme.palette.error['main'],
          },

          display: 'flex',
          columnGap: theme.spacing(3),
          alignItems: 'center',
          marginTop: theme.spacing(2),
          [theme.breakpoints.up('lg')]: {
            marginTop: theme.spacing(3),
          },
        },
      },
    },
  },
});
